import { Box, Button, Image, Tabs } from "@mantine/core";
import dayjs from "dayjs";
import { useForm } from "@mantine/form";
import { showNotification } from "@mantine/notifications";
import { IconAlertTriangle } from "@tabler/icons-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useExportAccountTransactionHistoryMutation,
  useExportTransactionHistoryMutation,
} from "@/services/dashboardAPI";
import { useDisclosure } from "@mantine/hooks";
import ExportAccountTransactionHistory from "./ExportAccountTransactionHistory";
import ImgExportFile from "@/assets/images/pngs/exportfile.png";
import ViewStatement from "./ViewStatement";
import ViewTrans from "./ViewTrans";

type PropHistory = {
  defaultValue: any;
  accountId: any;
  setForm?: (value: any) => void;
  sourceLinkId: string;
  title?: string;
  description?: string;
};

const tabSx = {
  fontSize: 16,
  "&[data-active]": {
    color: "#2A81D0",
    fontWeight: 600,
    borderBottom: "2px solid #2A81D0",
  },
  "&[data-active]:hover": {
    borderBottom: "2px solid #2A81D0",
  },
  height: "64px",
};

const TabsHistory = ({
  defaultValue,
  accountId,
  setForm,
  sourceLinkId,
  title,
  description,
}: PropHistory) => {
  const { t } = useTranslation();

  const [activeTab, setActiveTab] = useState(defaultValue);
  const [titlePopup, setTitlePopup] = useState<string | undefined>(undefined);
  console.log("activeTab", activeTab);

  const [
    isOpenContractInformation,
    { open: openContractInformation, close: closeContractInformation },
  ] = useDisclosure(false);

  const [exportAccountTransactionHistory, { isLoading }] =
    useExportAccountTransactionHistoryMutation();

  const [exportTransactionHistory, { isLoading: isLoadingTransaction }] =
    useExportTransactionHistoryMutation();

  // fileUrl lấy từ API trả về
  const [exportFileUrl, setExportFileUrl] = useState<string>("");

  // Khoảng thời gian đang xem trên tab Xem sao kê
  const [statementDateRange, setStatementDateRange] = useState({
    from: dayjs().subtract(30, "day").format("YYYYMMDD"),
    to: dayjs().format("YYYYMMDD"),
  });

  // ===== FORM FILTER =====
  const today = new Date();
  const maxDays = 180;

  const addDays = (date: Date, days: number): Date => {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
  };

  const form = useForm<{
    fromDate: Date | null;
    toDate: Date;
  }>({
    initialValues: {
      fromDate: null,
      toDate: today,
    },
  });

  const handleFromDateChange = (value: Date | null) => {
    const { toDate } = form.values;
    if (!value) return;

    if (value > toDate) {
      showNotification({
        title: t("ERROR"),
        message:
          t("FROM_DATE_CANNOT_BE_AFTER_TO_DATE") ||
          `${t("START_DATE_AFTER_END_DATE")}`,
        color: "red",
        icon: <IconAlertTriangle size={18} />,
      });
      return;
    }
    form.setFieldValue("fromDate", value);
  };

  const handleToDateChange = (value: Date | null) => {
    if (!value) return;

    form.setFieldValue("toDate", value);

    const { fromDate } = form.values;
    const minFromDate = addDays(value, -maxDays);
    if (fromDate && (fromDate > value || fromDate < minFromDate)) {
      form.setFieldValue("fromDate", null);
    }
  };

  const handleRefreshForm = () => {
    form.reset();
  };

  const handleSearchForm = () => {
    setForm?.(form.values);
  };

  const downloadFile = (url: string) => {
    const link = document.createElement("a");
    link.href = url;
    link.download = url.split("/").pop() || "file.pdf";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Payload:
  const payload = useMemo(
    () => ({
      accountNo: accountId,
      fromDate: "2025-12-23T00:00:00+00:00",
      toDate: "2025-12-23T00:00:00+00:00",
      fileType: "PDF",
    }),
    [accountId],
  );

  // CALL API + OPEN POPUP Transaction History
  const handleOpenDownloadTransactionHistory = async () => {
    openContractInformation();
    setExportFileUrl("");
    const dateRange = activeTab === "statement" ? statementDateRange : {
      from: dayjs().subtract(30, "day").format("YYYYMMDD"),
      to: dayjs().format("YYYYMMDD"),
    };
    const payloadTransactionHistory = {
      sourceLinkId,
      fromDate: dateRange.from,
      toDate: dateRange.to,
      minAmount: "",
      maxAmount: "",
      transType: "",
      amountType: "",
      pageNo: 1,
      pageSize: 1000,
      type: activeTab === "trans" ? "HISTORY" : "STATEMENT",
    };
    try {
      if (activeTab === "trans") {
        setTitlePopup(`${t("EXPORT_TRANSACTION_HISTORY")}`);
      } else {
        setTitlePopup(`${t("EXPORT_STATEMENT")}`);
      }
      const res = await exportTransactionHistory(
        payloadTransactionHistory,
      ).unwrap();

      const fileUrl =
        res?.body?.fileUrl || res?.body?.data?.fileUrl || res?.fileUrl || "";

      if (!fileUrl) {
        const resultCode = res?.body?.resultCode;
        if (resultCode !== "0") {
          showNotification({
            title: t("ERROR"),
            message: `${t("NO_FILE_URL_FROM_SYSTEM")}`,
            color: "red",
          });
        }
        return;
      }

      setExportFileUrl(fileUrl);
    } catch (error) {
      console.error("Export failed:", error);
      showNotification({
        title: t("ERROR"),
        message: `${t("EXPORT_FILE_FAILED_WITH_RETRY")}`,
        color: "red",
      });
    }
  };

  const renderActionButtons = () => {
    return (
      <Button
        variant="outline"
        leftIcon={<Image src={ImgExportFile} alt="Export File" />}
        style={{ borderRadius: "8px" }}
        onClick={handleOpenDownloadTransactionHistory}
      >
        {t("EXPORT_FILE")}
      </Button>
    );
  };

  return (
    <>
      <Tabs
        styles={{
          root: {
            background: "#FDFDFD",
            borderRadius: "8px",
            boxShadow: "0px 2px 8px 0px #638DBF1A",
            margin: "24px 0px",
          },
        }}
        defaultValue={defaultValue}
        value={activeTab}
        onTabChange={setActiveTab}
        keepMounted={false}
      >
        <Tabs.List
          sx={() => ({
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            "&::before": { display: "none" },
            "&::after": { display: "none" },
            borderBottom: "none",
          })}
        >
          <Tabs.Tab sx={tabSx} value="trans">
            {t("TRANSACTION_HISTORY")}
          </Tabs.Tab>
          <Tabs.Tab sx={tabSx} value="statement">
            {t("VIEW_STATEMENT")}
          </Tabs.Tab>

          <Box
            sx={() => ({
              marginLeft: "auto",
              marginRight: "20px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            })}
          >
            {renderActionButtons()}
          </Box>
        </Tabs.List>
        <Tabs.Panel value="trans" px={16}>
          <ViewTrans
            sourceLinkId={sourceLinkId}
            accountNo={accountId}
            title={title}
            description={description}
          />
        </Tabs.Panel>
        <Tabs.Panel value="statement" px={16}>
          <ViewStatement
            sourceLinkId={sourceLinkId}
            accountNo={accountId}
            onDateRangeChange={(from, to) => setStatementDateRange({ from, to })}
          />
        </Tabs.Panel>
      </Tabs>

      {/* POPUP DOWNLOAD */}
      <ExportAccountTransactionHistory
        opened={isOpenContractInformation}
        onClose={closeContractInformation}
        fileUrl={exportFileUrl}
        loading={isLoading || isLoadingTransaction}
        title={titlePopup}
        onDowload={() => {
          if (exportFileUrl) downloadFile(exportFileUrl);
        }}
      />
    </>
  );
};

export default TabsHistory;
